import { Link } from 'react-router-dom';
import { ChevronLeft, House, Infinity, Maximize2, Minimize2 } from 'lucide-react';
import { generateWorkLink } from '@/utils/utils';
import { getCapNome, getCapNum } from '@/utils/capitulo-fields';

interface ChapterNavbarProps {
    capitulo: any;
    visible?: boolean;
    infiniteScroll: boolean;
    onToggleInfiniteScroll: () => void;
    isFullscreen?: boolean;
    onToggleFullscreen?: () => void;
    showFullscreen?: boolean;
    /** Desktop: faixa de abas de leitura acima do header — desloca o header para baixo. */
    stackedReadingTabs?: boolean;
}

function tituloObra(obra: any | null | undefined): string {
    if (!obra) return '';
    return String(obra.nome ?? obra.obr_nome ?? obra.title ?? '').trim();
}

function rotuloCapituloAtual(capitulo: any): string {
    const nome = getCapNome(capitulo);
    const num = getCapNum(capitulo);
    if (nome && nome !== String(num)) return nome;
    if (num != null) return `Cap. ${num}`;
    return 'Capítulo';
}

export default function ChapterNavbar({
    capitulo,
    visible = true,
    infiniteScroll,
    onToggleInfiniteScroll,
    isFullscreen = false,
    onToggleFullscreen,
    showFullscreen = false,
    stackedReadingTabs = false,
}: ChapterNavbarProps) {
    const obraRef = capitulo?.obra;
    const obraId = obraRef?.id ?? obraRef?.obr_id;
    const nomeObra = tituloObra(obraRef);
    const obraHref = obraId != null ? generateWorkLink(obraId) : '/';
    const capLabel = rotuloCapituloAtual(capitulo);

    return (
        <header
            className={`fixed left-0 right-0 z-50 h-14 flex items-center border-b border-white/5 bg-black/50 backdrop-blur-xl transition-transform duration-300 ${
                stackedReadingTabs ? 'top-0 md:top-9' : 'top-0'
            } ${visible ? 'translate-y-0' : '-translate-y-full'}`}
        >
            <div className="max-w-4xl mx-auto w-full px-3 sm:px-4 flex items-center justify-between gap-3 min-w-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Link
                        to={obraHref}
                        className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors shrink-0"
                        title={nomeObra ? `Ir para ${nomeObra}` : 'Ir para a obra'}
                    >
                        <ChevronLeft size={20} className="shrink-0" />
                        <span className="text-xs sm:text-sm font-medium truncate max-w-[120px] sm:max-w-[220px] md:max-w-[280px]">
                            {nomeObra || 'Obra'}
                        </span>
                    </Link>
                    <span className="text-white/25 shrink-0" aria-hidden>
                        ·
                    </span>
                    <span className="text-sm font-semibold text-orange-400 truncate min-w-0" title={capLabel}>
                        {capLabel}
                    </span>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                    <button
                        type="button"
                        onClick={onToggleInfiniteScroll}
                        className={`p-2 rounded-lg transition-colors shrink-0 ${
                            infiniteScroll
                                ? 'text-orange-400 bg-orange-500/15 hover:bg-orange-500/25'
                                : 'text-white/70 hover:text-white hover:bg-white/10'
                        }`}
                        title={infiniteScroll ? 'Desligar leitura infinita' : 'Ligar leitura infinita'}
                        aria-label={infiniteScroll ? 'Desligar leitura infinita' : 'Ligar leitura infinita'}
                        aria-pressed={infiniteScroll}
                    >
                        <Infinity size={18} />
                    </button>
                    {showFullscreen && onToggleFullscreen && (
                        <button
                            type="button"
                            onClick={onToggleFullscreen}
                            className={`p-2 rounded-lg transition-colors shrink-0 ${
                                isFullscreen
                                    ? 'text-orange-400 bg-orange-500/15 hover:bg-orange-500/25'
                                    : 'text-white/70 hover:text-white hover:bg-white/10'
                            }`}
                            title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                            aria-label={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                            aria-pressed={isFullscreen}
                        >
                            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                        </button>
                    )}
                    <Link
                        to="/"
                        className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                        title="Ir para home"
                        aria-label="Ir para home"
                    >
                        <House size={18} />
                    </Link>
                </div>

            </div>
        </header>
    );
}
